import React from 'react'
import imageUrlBuilder from '@sanity/image-url'

// @ts-expect-error -- @sanity/client@0.144.0 does not provide TypeScript declarations
import sanityClient from '@sanity/client'
import useEmblaCarousel from 'embla-carousel-react'
import './care-team-carousel.css'

import { CareTeamProps } from './CareTeam'
import LeftArrowIcon from 'src/styles/icons/LeftArrowIcon'
import RightArrowIcon from 'src/styles/icons/RightArrowIcon'
import { getVisibleSpecialties } from './utils'

const client = sanityClient({
  projectId: process.env.GATSBY_SANITY_PROJECT_ID!,
  dataset: process.env.GATSBY_SANITY_DATASET!,
  useCdn: false,
})

export const CareTeamCarousel: React.FC<{ data: CareTeamProps }> = ({
  data,
}) => {
  const builder = imageUrlBuilder(client)
  const { title, subtitle, providers, backgroundColor, spacing = 'pyl' } = data

  const getImageUrl = (asset: any) => (asset ? builder.image(asset).url() : '')

  const providerCount = providers?.length || 0
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: providerCount > 1,
    align: 'start',
    containScroll: 'trimSnaps',
  })

  const scrollPrev = () => {
    emblaApi?.scrollPrev()
  }

  const scrollNext = () => {
    emblaApi?.scrollNext()
  }

  return (
    <section
      id={data.options?.uid || data.deepLinkId}
      className={`care-team-carousel__background ${
        backgroundColor || ''
      } ${spacing}`}
    >
      <div className="care-team-carousel__container">
        {(title || subtitle) && (
          <div className="care-team__heading">
            {title && <h2 className="care-team-carousel__title">{title}</h2>}
            {subtitle && (
              <p className="care-team-carousel__subtitle">{subtitle}</p>
            )}
          </div>
        )}
        <div className="care-team-carousel__carousel-wrapper" ref={emblaRef}>
          <div className="care-team-carousel__carousel-container">
            {(providers ?? [])?.map((provider: any) => {
              const main = provider?.content?.main
              const slug = main?.slug?.current
              const providerURL = `/team/${slug}/`
              const imageUrl = getImageUrl(main?.image?.asset)

              const { visible, remainingCount } = getVisibleSpecialties(
                main?.specialties || [],
                5
              )

              return (
                <div
                  className="care-team-carousel__carousel-slide"
                  key={provider?._key}
                >
                  <div className="care-team-carousel__card_item">
                    <div className="care-team-carousel__provider_details">
                      <div className="care-team-carousel__provider_image_container">
                        <img
                          className="care-team-carousel__provider_image"
                          src={imageUrl || ''}
                          alt={main?.title || 'Care team provider'}
                        />
                      </div>
                      <div className="care-team-carousel__provider_meta">
                        <div className="care-team-carousel__provider_title">
                          {main?.title}
                        </div>
                        <div className="care-team-carousel__specialties_container">
                          {visible?.map((specialty: string, index: number) => (
                            <div
                              key={index}
                              className="care-team-carousel__specialty"
                            >
                              {specialty}
                            </div>
                          ))}
                          {remainingCount > 0 && (
                            <div className="care-team-carousel__specialty">
                              +{remainingCount}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="care-team-carousel__actions">
                      <a
                        className="care-team-carousel__learn_more"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={providerURL}
                      >
                        View profile
                      </a>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        {providerCount > 1 && (
          <div className="care-team-carousel__carousel-controls">
            <button
              type="button"
              aria-label="Scroll Left"
              onClick={scrollPrev}
              className="care-team-carousel__carousel-prev"
            >
              <LeftArrowIcon />
            </button>

            <button
              type="button"
              aria-label="Scroll Right"
              onClick={scrollNext}
              className="care-team-carousel__carousel-next"
            >
              <RightArrowIcon />
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

export type { CareTeamProps }
